import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { authService } from '../services/authService';
import reportService from '../services/reportService';
import api from '../services/api';
import { machineryService } from '../services/machineryService';
import { useModuleSettings } from '../contexts/ModuleSettingsContext';
import Toast from '../components/Common/Toast';
import { formatDateLocaleLima } from '../utils/dateUtils';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [showSustentoModal, setShowSustentoModal] = useState(false);
  const [sustentoYear, setSustentoYear] = useState(new Date().getFullYear());
  const [sustentoMonth, setSustentoMonth] = useState(new Date().getMonth() + 1);
  const [sustentoLoading, setSustentoLoading] = useState(false);
  const [sustentoData, setSustentoData] = useState(null);
  const navigate = useNavigate();
  const hasLoggedApi = useRef(false);
  const hasLoadedStats = useRef(false);
  const { machineryEnabled } = useModuleSettings();

  const isLimitedUser = user?.role === 'limitado';
  const limitedWarehouseIds = Array.isArray(user?.warehouse_ids) ? user.warehouse_ids : [];
  const kardexLink =
    isLimitedUser && limitedWarehouseIds.length === 1
      ? `/reports/kardex?warehouse_id=${limitedWarehouseIds[0]}`
      : '/reports/kardex';

  useEffect(() => {
    // Obtener usuario solo del localStorage (guardado en el login)
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    } else {
      // Si no hay usuario en localStorage, redirigir a login
      navigate('/login');
    }

    // Obtener la URL base del API solo una vez (solo para logs en consola)
    if (!hasLoggedApi.current) {
      const baseURL = api.defaults.baseURL;
      console.log('🔗 [Dashboard] Conectando a:', baseURL);
      hasLoggedApi.current = true;
    }

    // Cargar estadísticas
    if (!hasLoadedStats.current && currentUser) {
      hasLoadedStats.current = true;
      loadDashboardStats();
    }
  }, [navigate]);

  useEffect(() => {
    if (machineryEnabled) {
      loadMaintenanceAlerts();
      loadComplianceAlerts();
    } else {
      setMaintenanceAlerts([]);
      setComplianceAlerts([]);
    }
  }, [machineryEnabled]);

  const loadMaintenanceAlerts = async () => {
    try {
      const response = await machineryService.getMaintenanceAlerts();
      if (response.success && Array.isArray(response.data)) {
        // Mostrar alerta cuando queda 10% o menos (pronto) o ya venció (alerta)
        const alerts = response.data.filter(item => item.status === 'pronto' || item.status === 'alerta');
        setMaintenanceAlerts(alerts);
      }
    } catch (error) {
      console.error('Error al cargar alertas de mantenimiento:', error);
    }
  };

  const loadComplianceAlerts = async () => {
    try {
      const response = await machineryService.getComplianceAlerts();
      if (response.success && Array.isArray(response.data)) {
        const alerts = response.data.filter(item => item.status === 'pronto' || item.status === 'alerta');
        setComplianceAlerts(alerts);
      }
    } catch (error) {
      console.error('Error al cargar alertas de SOAT/revisión técnica:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await reportService.getDashboardStats();
      if (response.success) {
        setStats(response.data);
      } else {
        setToast({
          isOpen: true,
          message: 'Error al cargar estadísticas del dashboard',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar estadísticas del dashboard',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatMaintenanceReading = (value, unit) => {
    if (value == null) return '—';
    if (unit === 'km') return Number(value).toLocaleString('es-PE');
    return Number(value).toLocaleString('es-PE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };

  const getGuideTypeLabel = (type) => {
    const labels = {
      'compra': 'Compra',
      'transferencia_gratuita': 'Transferencia Gratuita',
      'traspaso': 'Traspaso',
      'recepcion': 'Recepción',
      'salida': 'Salida',
      'inicial': 'Inicial'
    };
    return labels[type] || type;
  };

  const getMovementTypeLabel = (type) => {
    return type === 'entrada' ? 'Entrada' : 'Salida';
  };

  const getEventLabel = (event) => {
    const labels = {
      'compra': 'Compra',
      'transferencia_gratuita': 'Transferencia Gratuita',
      'traspaso': 'Traspaso',
      'recepcion': 'Recepción',
      'salida': 'Salida',
      'inicial': 'Inicial'
    };
    return labels[event] || event;
  };

  const formatMoney = (value) => {
    return `S/ ${Number(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const WAREHOUSE_CHART_COLORS = [
    '#10B981',
    '#3B82F6',
    '#8B5CF6',
    '#F59E0B',
    '#EF4444',
    '#06B6D4',
    '#EC4899',
    '#14B8A6',
  ];

  const buildWarehouseChartData = (warehouseStock = []) => {
    return warehouseStock.map((warehouse) => {
      const rawValue = Number(warehouse.total_value || 0);
      return {
        ...warehouse,
        rawValue,
        value: Math.abs(rawValue),
      };
    });
  };

  const buildWarehousePieChartData = (warehouseStock = [], minShare = 0.02) => {
    const items = buildWarehouseChartData(warehouseStock);
    const total = items.reduce((sum, item) => sum + item.value, 0);
    if (total <= 0) return [];

    const mainSlices = [];
    const smallSlices = [];

    items.forEach((item) => {
      const share = item.value / total;
      if (share >= minShare) {
        mainSlices.push(item);
      } else {
        smallSlices.push(item);
      }
    });

    if (smallSlices.length === 0) {
      return mainSlices.sort((a, b) => b.value - a.value);
    }

    const otrosSlice = {
      id: 'otros',
      name: `Otros (${smallSlices.length})`,
      code: '',
      rawValue: smallSlices.reduce((sum, item) => sum + item.rawValue, 0),
      value: smallSlices.reduce((sum, item) => sum + item.value, 0),
      otrosDetail: smallSlices.map((item) => `${item.name} (${formatMoney(item.rawValue)})`),
    };

    return [...mainSlices, otrosSlice].sort((a, b) => b.value - a.value);
  };

  const getWarehouseValueShare = (warehouseValue, warehouseStock = []) => {
    const totalAbsValue = warehouseStock.reduce(
      (sum, warehouse) => sum + Math.abs(Number(warehouse.total_value || 0)),
      0
    );
    if (totalAbsValue <= 0) return '0.0';
    return ((Math.abs(Number(warehouseValue || 0)) / totalAbsValue) * 100).toFixed(1);
  };

  const monthOptions = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  const yearOptions = Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - i);

  const cargarSustento = async (year = sustentoYear, month = sustentoMonth) => {
    try {
      setSustentoLoading(true);
      const response = await reportService.getDashboardSupportDocuments(year, month);
      if (response.success) {
        setSustentoData(response.data);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'No se pudo cargar el sustento',
          type: 'error',
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el sustento del gráfico',
        type: 'error',
      });
    } finally {
      setSustentoLoading(false);
    }
  };

  const abrirSustento = async () => {
    setShowSustentoModal(true);
    await cargarSustento();
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-screen text-lg text-gray-600">
        Cargando...
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">¡Bienvenido al Sistema Logístico!</h2>
          <p className="text-gray-600">
            {isLimitedUser
              ? 'Resumen de compras, almacenes asignados, movimientos y guías'
              : 'Resumen general del sistema'}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <p className="ml-3 text-gray-600">Cargando estadísticas...</p>
          </div>
        ) : stats ? (
          <>
            {/* Alertas de mantenimiento: solo si el módulo Maquinarias está activo */}
            {!isLimitedUser && machineryEnabled && maintenanceAlerts.length > 0 ? (
              <div className="mb-8">
                <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-sm p-6 border-l-4 border-amber-500">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
                      <span className="text-xl">🔔</span> Alertas de mantenimiento
                    </h3>
                    <Link to="/machinery/maintenance" className="text-sm font-medium text-amber-700 hover:text-amber-900">
                      Ver todas →
                    </Link>
                  </div>
                  <p className="text-sm text-amber-800 mb-4">Equipos con mantenimiento próximo (≤10% restante) o vencido.</p>
                  <div className="space-y-3">
                    {maintenanceAlerts.map((item, idx) => (
                      <div key={`${item.type}-${item.id}-${item.unit}-${idx}`} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">{item.code} — {item.name}</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                              {item.type === 'vehicle' ? 'Vehículo' : 'Máquina'} • {item.unit}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                              item.status === 'alerta' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {item.status === 'alerta' ? 'Vencido' : 'Pronto (≤10% restante)'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Lectura actual: {formatMaintenanceReading(item.current_reading, item.unit)} {item.unit}
                            {item.unit === 'km' && item.remaining_km != null && (
                              <span className="ml-2">• Faltan {formatMaintenanceReading(item.remaining_km, 'km')} km</span>
                            )}
                            {item.unit === 'horas' && item.remaining_hours != null && (
                              <span className="ml-2">• Faltan {formatMaintenanceReading(item.remaining_hours, 'horas')} h</span>
                            )}
                            {item.status === 'alerta' && <span className="ml-2 text-red-600">• ¡Requiere mantenimiento!</span>}
                          </div>
                        </div>
                        <Link
                          to={item.type === 'vehicle' ? `/machinery/vehicles/${item.id}/edit` : `/machinery/machines/${item.id}/edit`}
                          className="text-sm font-medium text-amber-700 hover:text-amber-900 whitespace-nowrap ml-2"
                        >
                          Configurar
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Alertas SOAT y revisión técnica */}
            {!isLimitedUser && machineryEnabled && complianceAlerts.length > 0 ? (
              <div className="mb-8">
                <div className="bg-red-50 border border-red-200 rounded-lg shadow-sm p-6 border-l-4 border-red-500">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-red-900 flex items-center gap-2">
                      <span className="text-xl">📋</span> SOAT y revisión técnica
                    </h3>
                    <Link to="/machinery/vehicles/compliance" className="text-sm font-medium text-red-700 hover:text-red-900">
                      Ver todas →
                    </Link>
                  </div>
                  <p className="text-sm text-red-800 mb-4">Documentos por vencer en los próximos 30 días o ya vencidos.</p>
                  <div className="space-y-3">
                    {complianceAlerts.map((item, idx) => (
                      <div key={`compliance-${item.id}-${item.document_type}-${idx}`} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">{item.code} — {item.name}</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                              {item.document_label}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                              item.status === 'alerta' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {item.status === 'alerta' ? 'Vencido' : 'Por vencer (≤ 30 días)'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Vence: {formatDateLocaleLima(item.expires_at)}
                            {item.days_remaining < 0 ? (
                              <span className="ml-2 text-red-600">• Vencido hace {Math.abs(item.days_remaining)} día(s)</span>
                            ) : (
                              <span className="ml-2">• Faltan {item.days_remaining} día(s)</span>
                            )}
                          </div>
                        </div>
                        <Link
                          to={`/machinery/vehicles/${item.id}/edit`}
                          className="text-sm font-medium text-red-700 hover:text-red-900 whitespace-nowrap ml-2"
                        >
                          Renovar
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Stats Cards - Totales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-teal-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Productos</p>
                    <p className="text-3xl font-bold text-teal-600">{stats.totals.products || 0}</p>
                  </div>
                  <div className="text-4xl">📦</div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Documentos</p>
                    <p className="text-3xl font-bold text-indigo-600">{stats.totals.documents || 0}</p>
                  </div>
                  <div className="text-4xl">🧾</div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Guías</p>
                    <p className="text-3xl font-bold text-green-600">{stats.totals.entry_guides || 0}</p>
                  </div>
                  <div className="text-4xl">📋</div>
                </div>
              </div>
            </div>

            {/* Gráfico de Torta - Valor por Almacén */}
            {stats.warehouse_stock && stats.warehouse_stock.length > 0 && (() => {
              const warehouseChartData = buildWarehousePieChartData(stats.warehouse_stock);
              const chartTotalAbs = warehouseChartData.reduce((sum, item) => sum + item.value, 0);

              return (
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Distribución de Valor por Almacén</h3>
                  <Link to={kardexLink} className="text-sm text-teal-600 hover:text-teal-700 font-medium">
                    Ver Kardex {isLimitedUser ? 'de su almacén' : 'completo'} →
                  </Link>
                </div>
                {warehouseChartData.some((item) => item.id === 'otros') ? (
                  <p className="text-xs text-gray-500 mb-4">
                    Los almacenes con participación menor al 2% se agrupan en «Otros». Pase el cursor sobre esa porción para ver el detalle.
                  </p>
                ) : null}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Gráfico de Torta */}
                  <div className="flex items-center justify-center min-h-[320px]">
                    {chartTotalAbs > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <PieChart>
                        <Pie
                          data={warehouseChartData}
                          cx="42%"
                          cy="50%"
                          labelLine={false}
                          label={false}
                          minAngle={4}
                          outerRadius={95}
                          innerRadius={42}
                          fill="#8884d8"
                          dataKey="value"
                          paddingAngle={1}
                        >
                          {warehouseChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${entry.id ?? index}`}
                              fill={
                                entry.id === 'otros'
                                  ? '#94A3B8'
                                  : WAREHOUSE_CHART_COLORS[index % WAREHOUSE_CHART_COLORS.length]
                              }
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(_value, _name, item) => {
                            if (item?.payload?.otrosDetail?.length) {
                              return [item.payload.otrosDetail.join(' · '), 'Almacenes menores'];
                            }
                            const share = chartTotalAbs > 0
                              ? ((item?.payload?.value ?? 0) / chartTotalAbs * 100).toFixed(1)
                              : '0.0';
                            return [`${formatMoney(item?.payload?.rawValue ?? 0)} (${share}%)`, 'Valor'];
                          }}
                        />
                        <Legend
                          layout="vertical"
                          align="right"
                          verticalAlign="middle"
                          wrapperStyle={{ fontSize: 12, lineHeight: '20px', paddingLeft: 12 }}
                          formatter={(value, entry) => {
                            const share = chartTotalAbs > 0
                              ? ((entry?.payload?.value ?? 0) / chartTotalAbs * 100).toFixed(1)
                              : '0.0';
                            return `${value} (${share}%)`;
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-gray-500 text-center px-6">
                        No hay valores distintos de cero para graficar.
                      </p>
                    )}
                  </div>
                  
                  {/* Lista de Almacenes con Valores */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Detalle por Almacén</h4>
                    {stats.warehouse_stock.map((warehouse, index) => {
                      const percentage = getWarehouseValueShare(warehouse.total_value, stats.warehouse_stock);
                      const rawValue = Number(warehouse.total_value || 0);
                      
                      return (
                        <div key={warehouse.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: WAREHOUSE_CHART_COLORS[index % WAREHOUSE_CHART_COLORS.length] }}
                            ></div>
                            <div>
                              <div className="font-medium text-gray-900">{warehouse.name}</div>
                              <div className="text-xs text-gray-500">{warehouse.code}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`font-bold ${rawValue < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                              {formatMoney(rawValue)}
                            </div>
                            <div className="text-xs text-gray-500">{percentage}%</div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">Total:</span>
                        <span className={`font-bold ${
                          stats.warehouse_stock.reduce((sum, w) => sum + (w.total_value || 0), 0) < 0
                            ? 'text-red-600'
                            : 'text-teal-600'
                        }`}>
                          {formatMoney(stats.warehouse_stock.reduce((sum, w) => sum + (w.total_value || 0), 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* Control de compras — gráfico último semestre */}
            {stats.semester_products_services && stats.semester_products_services.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      {isLimitedUser ? 'Control de compras - Último semestre' : 'Productos y Servicios - Último Semestre'}
                    </h3>
                    <p className="text-sm text-gray-500 italic">
                      {isLimitedUser
                        ? 'Montos de productos y servicios según los documentos de compra que usted registró (emitidos).'
                        : 'Nota: Los datos mostrados se obtienen de los documentos emitidos del sistema.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium rounded-md bg-teal-600 text-white hover:bg-teal-700 shrink-0"
                    onClick={abrirSustento}
                  >
                    Sustentar
                  </button>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.semester_products_services}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month_name" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `S/ ${value.toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                    />
                    <Tooltip 
                      formatter={(value, name) => {
                        const label = name === 'products' ? 'Productos' : 'Servicios';
                        const formattedValue = `S/ ${parseFloat(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        return [formattedValue, label];
                      }}
                    />
                    <Legend 
                      formatter={(value) => {
                        return value === 'products' ? 'Productos' : 'Servicios';
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="products" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      name="products"
                      dot={{ r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="services" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      name="services"
                      dot={{ r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Grid de 2 columnas: Guías Recientes y Movimientos Recientes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Guías Recientes */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Guías Recientes</h3>
                  <Link to="/warehouses" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
                    Ver todas →
                  </Link>
                </div>
                {stats.recent.guides && stats.recent.guides.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent.guides.map((guide) => (
                      <div key={guide.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{guide.code}</span>
                            <span className="text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded">
                              {getGuideTypeLabel(guide.type)}
                            </span>
                            {guide.status === 'issued' && (
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">Emitida</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {guide.warehouse} {guide.supplier && `• ${guide.supplier}`}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {formatDateLocaleLima(guide.date)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No hay guías recientes</p>
                )}
              </div>

              {/* Movimientos Recientes */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Movimientos Recientes</h3>
                  <Link to={kardexLink} className="text-sm text-teal-600 hover:text-teal-700 font-medium">
                    Ver Kardex →
                  </Link>
                </div>
                {stats.recent.movements && stats.recent.movements.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent.movements.map((movement) => (
                      <div key={movement.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{movement.product}</span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              movement.type === 'entrada' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {getMovementTypeLabel(movement.type)}
                            </span>
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {getEventLabel(movement.event)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {movement.warehouse} • Cantidad: {parseFloat(movement.quantity).toFixed(3)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {formatDateLocaleLima(movement.date)} {movement.time}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No hay movimientos recientes</p>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500">No se pudieron cargar las estadísticas</p>
          </div>
        )}
      </div>

      {showSustentoModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setShowSustentoModal(false)}
          role="presentation"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Sustento del gráfico: Productos y Servicios</h3>
                <p className="text-sm text-gray-500">Seleccione mes y año para ver qué documentos componen el total.</p>
              </div>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                onClick={() => setShowSustentoModal(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4 border-b border-gray-100 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={sustentoMonth}
                  onChange={(e) => setSustentoMonth(Number(e.target.value))}
                >
                  {monthOptions.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={sustentoYear}
                  onChange={(e) => setSustentoYear(Number(e.target.value))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 flex md:justify-end">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
                  onClick={() => cargarSustento()}
                  disabled={sustentoLoading}
                >
                  {sustentoLoading ? 'Cargando...' : 'Ver sustento'}
                </button>
              </div>
            </div>

            <div className="px-5 py-4 overflow-y-auto flex-1">
              {sustentoData ? (
                <>
                  <div className="mb-3 text-sm text-gray-700">
                    <span className="font-medium">Periodo:</span> {sustentoData.month_name}
                    {' · '}
                    <span className="font-medium">Documentos:</span> {sustentoData.totals?.documents_count || 0}
                  </div>
                  <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                      <div className="text-xs text-emerald-800">Total Productos</div>
                      <div className="text-base font-semibold text-emerald-900">
                        {formatMoney(sustentoData.totals?.products)}
                      </div>
                    </div>
                    <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
                      <div className="text-xs text-blue-800">Total Servicios</div>
                      <div className="text-base font-semibold text-blue-900">
                        {formatMoney(sustentoData.totals?.services)}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-300 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-700">Total General</div>
                      <div className="text-base font-bold text-slate-900">
                        {formatMoney(sustentoData.totals?.overall)}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-md border border-gray-200 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Documento</th>
                          <th className="px-3 py-2 text-left font-medium">Fecha</th>
                          <th className="px-3 py-2 text-left font-medium">Tipo</th>
                          <th className="px-3 py-2 text-left font-medium">Proveedor</th>
                          <th className="px-3 py-2 text-right font-medium">Productos</th>
                          <th className="px-3 py-2 text-right font-medium">Servicios</th>
                          <th className="px-3 py-2 text-right font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(sustentoData.documents || []).length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                              No hay documentos en ese periodo.
                            </td>
                          </tr>
                        ) : (
                          (sustentoData.documents || []).map((row) => (
                            <tr key={`${row.source}-${row.document_id}`} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-gray-800">{row.document_code || `DOC-${row.document_id}`}</td>
                              <td className="px-3 py-2 text-gray-700">{row.document_date ? formatDateLocaleLima(row.document_date) : '—'}</td>
                              <td className="px-3 py-2">
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                  {row.source === 'suministro_continuo' ? 'Suministro continuo' : 'Documento'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-gray-700">{row.supplier_name || '—'}</td>
                              <td className="px-3 py-2 text-right">{formatMoney(row.products_total)}</td>
                              <td className="px-3 py-2 text-right">{formatMoney(row.services_total)}</td>
                              <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatMoney(row.total)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      {(sustentoData.documents || []).length > 0 ? (
                        <tfoot>
                          <tr className="bg-gray-50 border-t border-gray-200">
                            <td colSpan={4} className="px-3 py-2 font-semibold text-gray-800 text-right">
                              Totales
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-gray-900">
                              {formatMoney(sustentoData.totals?.products)}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-gray-900">
                              {formatMoney(sustentoData.totals?.services)}
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-gray-900">
                              {formatMoney(sustentoData.totals?.overall)}
                            </td>
                          </tr>
                        </tfoot>
                      ) : null}
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-500">Seleccione mes y año para ver el sustento.</div>
              )}
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Dashboard;
